import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/shared/ui/button'

interface ArticlePaginationProps {
  currentPage: number
  totalPages: number
  hasNextPage: boolean
  hasPrevPage: boolean
  onPageChange: (page: number) => void
}

export function ArticlePagination({
  currentPage,
  totalPages,
  hasNextPage,
  hasPrevPage,
  onPageChange,
}: ArticlePaginationProps) {
  const { t } = useTranslation('articles')

  // Don't render if there's only one page or no pages
  if (totalPages <= 1) return null

  // Generate visible page numbers (show up to 5 pages around current)
  const getVisiblePages = () => {
    const delta = 2
    const range = []
    const rangeWithDots = []

    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i)
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...')
    } else {
      rangeWithDots.push(1)
    }

    rangeWithDots.push(...range)

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages)
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages)
    }

    return rangeWithDots
  }

  const visiblePages = getVisiblePages()

  return (
    <nav
      className="flex items-center justify-center gap-1 py-8"
      role="navigation"
      aria-label={t('list.pagination.ariaLabel')}
    >
      {/* Previous button */}
      <Button
        variant={hasPrevPage ? 'primary' : 'outline'}
        size="sm"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={!hasPrevPage}
        className="mr-2"
        aria-label={t('list.pagination.goToPrevious')}
      >
        {t('list.pagination.previous')}
      </Button>

      {/* Page numbers */}
      <div className="flex items-center gap-1">
        {visiblePages.map((page, index) => (
          <Fragment key={index}>
            {page === '...' ? (
              <span className="text-muted-foreground px-3 py-2 text-sm" aria-hidden="true">
                ...
              </span>
            ) : (
              <Button
                variant={currentPage === page ? 'primary' : 'outline'}
                size="sm"
                onClick={() => typeof page === 'number' && onPageChange(page)}
                className={[
                  'min-w-[2.5rem]',
                  currentPage === page ? '' : 'hover:bg-accent hover:text-accent-foreground',
                ].join(' ')}
                aria-label={t('list.pagination.goToPage', { page })}
                aria-current={currentPage === page ? 'page' : undefined}
              >
                {page}
              </Button>
            )}
          </Fragment>
        ))}
      </div>

      {/* Next button */}
      <Button
        variant={hasNextPage ? 'primary' : 'outline'}
        size="sm"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={!hasNextPage}
        className="ml-2"
        aria-label={t('list.pagination.goToNext')}
      >
        {t('list.pagination.next')}
      </Button>
    </nav>
  )
}

export default ArticlePagination
